import { useMemo, useState } from 'react'
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  BookOpen,
  GraduationCap,
  Clock,
  Lightbulb,
  Wand2,
  Plus,
  Trash2,
  GripVertical,
  CheckCircle2,
  PanelRightClose,
  PanelRightOpen,
  FileDown,
  Save,
  Ellipsis,
  Users,
  Paperclip,
  MessageSquare,
  History,
  RotateCcw,
  X,
  Presentation,
} from 'lucide-react'
import { API_BASE_URL, USE_MOCK } from '../App'
import type { ActivityStep, DesignNote, DialogLine, LessonPhase, LessonResourceRecommendation, Resource } from '../types/lessonPlan'
import { parseLessonPlanToPhases } from '../utils/lessonPlanParser'

interface LessonPlanProps {
  onBack: () => void
}

const subjects = [
  { value: 'Mathematics', label: 'Mathematics', icon: '🔢' },
  { value: 'English Language Arts', label: 'ELA', icon: '📖' },
  { value: 'Science', label: 'Science', icon: '🔬' },
  { value: 'Social Studies', label: 'Social Studies', icon: '📜' },
  { value: 'Physics', label: 'Physics', icon: '⚡' },
  { value: 'Chemistry', label: 'Chemistry', icon: '🧪' },
  { value: 'Biology', label: 'Biology', icon: '🌱' },
  { value: 'History', label: 'History', icon: '🏛️' },
  { value: 'Geography', label: 'Geography', icon: '🌍' },
  { value: 'Computer Science', label: 'Computer Science', icon: '💻' },
  { value: 'Art', label: 'Art', icon: '🎨' },
  { value: 'Music', label: 'Music', icon: '🎵' },
  { value: 'Physical Education', label: 'Physical Education', icon: '⚽' },
  { value: 'Health', label: 'Health', icon: '❤️' },
  { value: 'Economics', label: 'Economics', icon: '📊' },
  { value: 'Psychology', label: 'Psychology', icon: '🧠' },
  { value: 'Spanish', label: 'Spanish', icon: '🇪🇸' },
  { value: 'French', label: 'French', icon: '🇫🇷' },
  { value: 'German', label: 'German', icon: '🇩🇪' },
  { value: 'Mandarin', label: 'Mandarin', icon: '🇨🇳' },
]

// US K-12 Grade levels
const grades = [
  'Kindergarten',
  '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
  '6th Grade', '7th Grade', '8th Grade',
  '9th Grade (Freshman)', '10th Grade (Sophomore)', '11th Grade (Junior)', '12th Grade (Senior)',
  'AP / Honors',
]

// US State Standards
const stateStandards = [
  { value: 'CCSS', label: 'Common Core State Standards', description: 'Math & ELA' },
  { value: 'NGSS', label: 'NGSS', description: 'Next Generation Science Standards' },
  { value: 'TEKS', label: 'TEKS', description: 'Texas Essential Knowledge & Skills' },
  { value: 'CA', label: 'California', description: 'California State Standards' },
  { value: 'NY', label: 'New York', description: 'NY State Learning Standards' },
  { value: 'FL', label: 'Florida', description: 'Florida B.E.S.T. Standards' },
  { value: 'GA', label: 'Georgia', description: 'Georgia Standards of Excellence' },
  { value: 'NC', label: 'North Carolina', description: 'NC Standard Course of Study' },
  { value: 'OH', label: 'Ohio', description: 'Ohio Learning Standards' },
  { value: 'PA', label: 'Pennsylvania', description: 'PA Core Standards' },
  { value: 'VA', label: 'Virginia', description: 'Virginia SOLs' },
  { value: 'OTHER', label: 'Other / General', description: 'General standards' },
]

const generateMockLessonPlan = (subject: string, grade: string, topic: string, duration: string) => {
  return `# ${topic} - Lesson Plan

## Overview
- **Subject**: ${subject}
- **Grade Level**: ${grade}
- **Duration**: ${duration} minutes
- **Topic**: ${topic}

## Learning Objectives
By the end of this lesson, students will be able to:
1. Understand the core concepts of ${topic}
2. Apply critical thinking skills to analyze ${topic.toLowerCase()}
3. Demonstrate knowledge through collaborative activities
4. Connect ${topic.toLowerCase()} to real-world applications

## Materials Needed
- Whiteboard and markers
- Student worksheets
- Digital presentation slides
- Handouts with key vocabulary
- Assessment rubrics

## Lesson Procedure

### Introduction (10 minutes)
- Greet students and review previous lesson
- Present the learning objectives
- Use a thought-provoking question to engage students: *"Have you ever wondered how ${topic.toLowerCase()} affects your daily life?"*
- Brief video clip or visual aid to spark interest

### Direct Instruction (15 minutes)
- Present key concepts using visual aids
- Define important vocabulary terms
- Model problem-solving strategies
- Check for understanding with guided questions

### Guided Practice (10 minutes)
- Students work in pairs on practice problems
- Teacher circulates to provide support
- Address common misconceptions
- Encourage peer collaboration

### Independent Practice (10 minutes)
- Students complete individual worksheets
- Apply learned concepts to new situations
- Teacher monitors progress and provides feedback

### Closure (5 minutes)
- Summarize key takeaways
- Exit ticket: Students write one thing they learned
- Preview next lesson
- Assign homework if applicable

## Differentiation Strategies
- Extension activities with increased complexity
- Opportunities for peer teaching
- Open-ended research projects
- Visual aids and graphic organizers
- Modified assignments with scaffolding
- Small group instruction

## Assessment
- Observation during guided practice
- Exit tickets
- Class discussions
- Think-pair-share activities

## Homework
- Review notes and vocabulary
- Complete practice worksheet
- Prepare questions for next class
- Optional: Research extension topic

## Reflection
- What worked well?
- What needs improvement?
- How can I better support diverse learners?

---
*Generated by EduSpark AI - Your Teaching Assistant*`
}

const createId = () => Math.random().toString(36).slice(2, 10)
const STORAGE_KEY = 'eduspark-lesson-workbench'
const HISTORY_KEY = 'eduspark-lesson-history'
const MAX_HISTORY = 20

interface LessonHistoryItem {
  id: string
  title: string
  subject: string
  grade: string
  topic: string
  createdAt: number
  phases: LessonPhase[]
  result: string
  formData: {
    subject: string
    grade: string
    topic: string
    instructions: string
    duration: string
  }
}

const loadHistory = (): LessonHistoryItem[] => {
  try {
    return JSON.parse(window.localStorage.getItem(HISTORY_KEY) || '[]')
  } catch {
    return []
  }
}

const saveToHistory = (item: LessonHistoryItem) => {
  const list = loadHistory().filter((h) => h.id !== item.id)
  list.unshift(item)
  window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, MAX_HISTORY)))
}

const formatTime = (ts: number) => {
  const d = new Date(ts)
  return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}

const phaseTypeStyles: Record<LessonPhase['type'], string> = {
  engage: 'border-blue-400/30 bg-blue-500/10 text-blue-300',
  inquire: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300',
  apply: 'border-orange-400/30 bg-orange-500/10 text-orange-300',
  assess: 'border-violet-400/30 bg-violet-500/10 text-violet-300',
}

const phaseTypeLabel: Record<LessonPhase['type'], string> = {
  engage: 'Engage',
  inquire: 'Inquire',
  apply: 'Apply',
  assess: 'Assess',
}

const statusStyles: Record<LessonPhase['status'], string> = {
  draft: 'bg-white/10 text-white/60',
  edited: 'bg-amber-500/20 text-amber-300',
  completed: 'bg-green-500/20 text-green-300',
}

const roleStyles: Record<DialogLine['role'], string> = {
  teacher: 'bg-blue-500/20 text-blue-300 border-blue-400/20',
  student: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/20',
  narrator: 'bg-violet-500/20 text-violet-300 border-violet-400/20',
}

const tabOptions = [
  { key: 'script', label: '📝 Lesson Script', icon: MessageSquare },
  { key: 'activities', label: '🎯 Student Activities', icon: Users },
  { key: 'resources', label: '📎 Teaching Resources', icon: Paperclip },
  { key: 'design', label: '💡 Design Notes', icon: Lightbulb },
] as const

type EditorTab = typeof tabOptions[number]['key']

type SupportKey = 'struggling' | 'advanced' | 'language'

// Activity suggestions based on interaction mode
const modeActivitySuggestions: Record<ActivityStep['mode'], string[]> = {
  individual: [
    'Independent reading and reflection',
    'Personal journal writing',
    'Individual problem-solving worksheet',
    'Self-paced online module',
    'Personal concept mapping',
    'Silent thinking time',
    'Individual research task',
  ],
  pair: [
    'Think-pair-share discussion',
    'Peer tutoring session',
    'Collaborative problem-solving',
    'Pair reading and summarizing',
    'Two-person debate',
    'Partner check and feedback',
    'Role-play with partner',
  ],
  group: [
    'Small group discussion (3-4 students)',
    'Collaborative project work',
    'Group brainstorming session',
    'Jigsaw activity',
    'Group presentation preparation',
    'Team problem-solving challenge',
    'Gallery walk in groups',
  ],
  class: [
    'Whole-class discussion',
    'Teacher-led demonstration',
    'Class poll or survey',
    'Socratic seminar',
    'Class debate',
    'Interactive lecture',
    'Whole-class Q&A session',
  ],
}

type WorkspaceSnapshot = {
  formData: {
    subject: string
    grade: string
    topic: string
    instructions: string
    duration: string
  }
  lessonTitle: string
  result: string
  phases: LessonPhase[]
}

function LessonPlan({ onBack }: LessonPlanProps) {
  const savedDraft = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
  const parsedDraft: WorkspaceSnapshot | null = savedDraft ? JSON.parse(savedDraft) : null

  const [formData, setFormData] = useState(parsedDraft?.formData ?? {
    subject: '',
    grade: '',
    topic: '',
    instructions: '',
    duration: '45',
  })
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(parsedDraft?.result ?? '')
  const [error, setError] = useState('')
  const [lessonTitle, setLessonTitle] = useState(parsedDraft?.lessonTitle ?? 'Untitled Lesson Plan')
  const [saveState, setSaveState] = useState<'saved' | 'saving'>('saved')
  const [phases, setPhases] = useState<LessonPhase[]>(parsedDraft?.phases ?? [])
  const [activeTab, setActiveTab] = useState<EditorTab>('script')
  const [draggingPhaseId, setDraggingPhaseId] = useState<string | null>(null)
  const [rightCollapsed, setRightCollapsed] = useState(false)
  const [openPanels, setOpenPanels] = useState({ support: true, settings: true, resources: true, export: false })
  const [supportOptions, setSupportOptions] = useState<Record<SupportKey, boolean>>({ struggling: false, advanced: false, language: false })
  const [history, setHistory] = useState<LessonPhase[][]>([])
  const [showCardMenuId, setShowCardMenuId] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [historyList, setHistoryList] = useState<LessonHistoryItem[]>(() => loadHistory())
  // originalResult: the raw markdown right after AI generation, used for "discard edits"
  const [originalResult, setOriginalResult] = useState(parsedDraft?.result ?? '')
  const [pptExportLoading, setPptExportLoading] = useState(false)
  const [pptData, setPptData] = useState<any>(null)
  const [showPPTModal, setShowPPTModal] = useState(false)
  
  // Edit & Regenerate modal state
  const [showEditModal, setShowEditModal] = useState(false)
  const [editFormData, setEditFormData] = useState(formData)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  
  // Differentiation state
  const [originalSupportOptions, setOriginalSupportOptions] = useState<Record<SupportKey, boolean>>({ struggling: false, advanced: false, language: false })
  const [showDifferentiationApply, setShowDifferentiationApply] = useState(false)
  const [differentiationLoading, setDifferentiationLoading] = useState(false)

  const hasGenerated = phases.length > 0
  const activePhase = phases.find((phase) => phase.isActive) || null
  const totalDuration = useMemo(() => phases.reduce((sum, phase) => sum + phase.duration, 0), [phases])
  
  // Check if differentiation options changed from original
  const hasDifferentiationChanges = useMemo(() => {
    return (Object.keys(supportOptions) as SupportKey[]).some(
      key => supportOptions[key] !== originalSupportOptions[key]
    )
  }, [supportOptions, originalSupportOptions])

  const persistDraft = (nextPhases = phases, nextResult = result, nextTitle = lessonTitle, nextFormData = formData) => {
    if (typeof window === 'undefined') return
    const snapshot: WorkspaceSnapshot = {
      formData: nextFormData,
      lessonTitle: nextTitle,
      result: nextResult,
      phases: nextPhases,
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
  }

  const markSaving = (nextPhases = phases, nextResult = result, nextTitle = lessonTitle, nextFormData = formData) => {
    setSaveState('saving')
    persistDraft(nextPhases, nextResult, nextTitle, nextFormData)
    window.clearTimeout((markSaving as unknown as { timer?: number }).timer)
    ;(markSaving as unknown as { timer?: number }).timer = window.setTimeout(() => setSaveState('saved'), 700)
  }

  const pushHistory = (snapshot: LessonPhase[]) => {
    setHistory((prev) => [...prev.slice(-19), JSON.parse(JSON.stringify(snapshot))])
  }
  
  // Handle opening edit modal
  const handleOpenEditModal = () => {
    setEditFormData(formData)
    setHasUnsavedChanges(false)
    setShowEditModal(true)
  }
  
  // Handle edit form changes
  const handleEditFormChange = (field: keyof typeof formData, value: string) => {
    setEditFormData(prev => ({ ...prev, [field]: value }))
    setHasUnsavedChanges(true)
  }
  
  // Handle regenerate with confirmation
  const handleRegenerateClick = () => {
    if (hasEdits) {
      setShowConfirmDialog(true)
    } else {
      submitRegenerate()
    }
  }
  
  // Submit regenerate request
  const submitRegenerate = async () => {
    setShowConfirmDialog(false)
    setShowEditModal(false)
    setLoading(true)
    setError('')
    
    // Update form data with edited values
    const newFormData = { ...editFormData }
    setFormData(newFormData)

    const applyGeneratedContent = (markdown: string) => {
      const nextPhases = parseLessonPlanToPhases(markdown, newFormData.subject, newFormData.topic)
      const title = newFormData.topic || 'Untitled Lesson Plan'
      setResult(markdown)
      setOriginalResult(markdown)
      setPhases(nextPhases)
      setLessonTitle(title)
      setSaveState('saved')
      setActiveTab('script')
      setHistory([])
      persistDraft(nextPhases, markdown, title, newFormData)
      // save to history
      const historyItem: LessonHistoryItem = {
        id: createId(),
        title,
        subject: newFormData.subject,
        grade: newFormData.grade,
        topic: newFormData.topic,
        createdAt: Date.now(),
        phases: nextPhases,
        result: markdown,
        formData: newFormData,
      }
      saveToHistory(historyItem)
      setHistoryList(loadHistory())
    }

    if (USE_MOCK) {
      setTimeout(() => {
        const mockResult = generateMockLessonPlan(newFormData.subject, newFormData.grade, newFormData.topic, newFormData.duration)
        applyGeneratedContent(mockResult)
        setLoading(false)
      }, 1200)
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/lesson-plans/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: newFormData.subject,
          grade: newFormData.grade,
          topic: newFormData.topic,
          duration: `${newFormData.duration} minutes`,
          instructions: newFormData.instructions,
        }),
      })

      if (!response.ok) throw new Error('Failed to generate lesson plan')
      const data = await response.json()
      if (data.success) {
        applyGeneratedContent(data.data)
      } else {
        throw new Error(data.error || 'Unknown error')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }
  
  // Apply differentiation changes
  const applyDifferentiation = async () => {
    setDifferentiationLoading(true)
    setError('')
    
    try {
      // Call API to regenerate differentiation section
      const response = await fetch(`${API_BASE_URL}/api/lesson-plans/differentiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lessonPlan: result,
          subject: formData.subject,
          grade: formData.grade,
          topic: formData.topic,
          differentiationOptions: supportOptions,
        }),
      })

      if (!response.ok) throw new Error('Failed to apply differentiation')
      const data = await response.json()
      if (data.success) {
        // Update the lesson plan with new differentiation content
        const updatedResult = data.data
        setResult(updatedResult)
        setOriginalResult(updatedResult)
        const nextPhases = parseLessonPlanToPhases(updatedResult, formData.subject, formData.topic)
        setPhases(nextPhases)
        persistDraft(nextPhases, updatedResult, lessonTitle, formData)
        setOriginalSupportOptions({ ...supportOptions })
        setShowDifferentiationApply(false)
      } else {
        throw new Error(data.error || 'Unknown error')
      }
    } catch (err) {
      // If API fails, just save the options locally
      setOriginalSupportOptions({ ...supportOptions })
      setShowDifferentiationApply(false)
      // Don't show error for now since the endpoint might not exist yet
    } finally {
      setDifferentiationLoading(false)
    }
  }
  
  // Insert activity suggestion based on interaction mode
  const insertActivitySuggestion = (suggestion: string) => {
    updateActivePhase((phase) => ({
      ...phase,
      activities: [...phase.activities, { id: createId(), action: suggestion, duration: 5, mode: phase.settings?.interactionMode || 'individual', completed: false }],
    }))
  }

  const setActivePhase = (phaseId: string) => {
    const next = phases.map((phase) => ({ ...phase, isActive: phase.id === phaseId }))
    setPhases(next)
    markSaving(next)
  }

  const updateActivePhase = (updater: (phase: LessonPhase) => LessonPhase) => {
    pushHistory(phases)
    const next = phases.map((phase) => {
      if (!phase.isActive) return phase
      const updated = updater(phase)
      return { ...updated, status: updated.status === 'completed' ? 'completed' : 'edited' }
    })
    setPhases(next)
    markSaving(next)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const applyGeneratedContent = (markdown: string) => {
      const nextPhases = parseLessonPlanToPhases(markdown, formData.subject, formData.topic)
      const title = formData.topic || 'Untitled Lesson Plan'
      setResult(markdown)
      setOriginalResult(markdown)
      setPhases(nextPhases)
      setLessonTitle(title)
      setSaveState('saved')
      setActiveTab('script')
      setHistory([])
      persistDraft(nextPhases, markdown, title, formData)
      // Reset differentiation tracking
      setOriginalSupportOptions({ struggling: false, advanced: false, language: false })
      setSupportOptions({ struggling: false, advanced: false, language: false })
      // save to history
      const historyItem: LessonHistoryItem = {
        id: createId(),
        title,
        subject: formData.subject,
        grade: formData.grade,
        topic: formData.topic,
        createdAt: Date.now(),
        phases: nextPhases,
        result: markdown,
        formData,
      }
      saveToHistory(historyItem)
      setHistoryList(loadHistory())
    }

    if (USE_MOCK) {
      setTimeout(() => {
        const mockResult = generateMockLessonPlan(formData.subject, formData.grade, formData.topic, formData.duration)
        applyGeneratedContent(mockResult)
        setLoading(false)
      }, 1200)
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/lesson-plans/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: formData.subject,
          grade: formData.grade,
          topic: formData.topic,
          duration: `${formData.duration} minutes`,
          instructions: formData.instructions,
        }),
      })

      if (!response.ok) throw new Error('Failed to generate lesson plan')
      const data = await response.json()
      if (data.success) {
        applyGeneratedContent(data.data)
      } else {
        throw new Error(data.error || 'Unknown error')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const addPhaseAt = (index = phases.length) => {
    pushHistory(phases)
    const phase: LessonPhase = {
      id: createId(),
      title: `New Phase ${phases.length + 1}`,
      type: 'engage',
      duration: 10,
      status: 'draft',
      isActive: true,
      script: [{ id: createId(), role: 'teacher', content: '' }],
      activities: [],
      resources: [],
      designNote: { objective: '', keyQuestions: '', misconceptions: '', notes: '' },
      recommendations: [],
      settings: { interactionMode: 'class', formativeAssessment: false, standards: [] },
    }
    const next = [...phases.map((p) => ({ ...p, isActive: false }))]
    next.splice(index, 0, phase)
    setPhases(next)
    markSaving(next)
  }

  const deletePhase = (phaseId: string) => {
    pushHistory(phases)
    const filtered = phases.filter((phase) => phase.id !== phaseId)
    const next = !filtered.length ? [] : filtered.some((phase) => phase.isActive) ? filtered : filtered.map((phase, index) => ({ ...phase, isActive: index === 0 }))
    setPhases(next)
    markSaving(next)
  }

  const duplicatePhase = (phaseId: string) => {
    pushHistory(phases)
    const index = phases.findIndex((phase) => phase.id === phaseId)
    if (index === -1) return
    const copy = JSON.parse(JSON.stringify(phases[index])) as LessonPhase
    copy.id = createId()
    copy.title = `${copy.title} Copy`
    copy.isActive = true
    copy.script = copy.script.map((item) => ({ ...item, id: createId() }))
    copy.activities = copy.activities.map((item) => ({ ...item, id: createId() }))
    copy.resources = copy.resources.map((item) => ({ ...item, id: createId() }))
    copy.recommendations = copy.recommendations?.map((item) => ({ ...item, id: createId() }))
    const next = phases.map((phase) => ({ ...phase, isActive: false }))
    next.splice(index + 1, 0, copy)
    setPhases(next)
    markSaving(next)
  }

  const movePhase = (fromId: string, toId: string) => {
    pushHistory(phases)
    const next = [...phases]
    const fromIndex = next.findIndex((phase) => phase.id === fromId)
    const toIndex = next.findIndex((phase) => phase.id === toId)
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)
    setPhases(next)
    markSaving(next)
  }

  const discardEdits = () => {
    if (!originalResult) return
    if (!window.confirm('Discard all manual edits and restore AI-generated content?')) return
    const nextPhases = parseLessonPlanToPhases(originalResult, formData.subject, formData.topic)
    setPhases(nextPhases)
    setHistory([])
    markSaving(nextPhases)
  }

  const restoreFromHistory = (item: LessonHistoryItem) => {
    if (!window.confirm(`Restore history lesson plan \"${item.title}\"? Current content will be overwritten.`)) return
    setResult(item.result)
    setOriginalResult(item.result)
    setPhases(item.phases)
    setLessonTitle(item.title)
    setFormData(item.formData)
    setSaveState('saved')
    setHistory([])
    setActiveTab('script')
    persistDraft(item.phases, item.result, item.title, item.formData)
    setShowHistory(false)
  }

  const deleteHistoryItem = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const next = historyList.filter((h) => h.id !== id)
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next))
    setHistoryList(next)
  }

  const hasEdits = useMemo(() => {
    if (!originalResult || !hasGenerated) return false
    return phases.some((p) => p.status === 'edited' || p.status === 'completed')
  }, [phases, originalResult, hasGenerated])

  const undoReorder = () => {
    const previous = history[history.length - 1]
    if (!previous) return
    const nextHistory = history.slice(0, -1)
    setHistory(nextHistory)
    setPhases(previous)
    markSaving(previous)
  }

  const exportPlaceholder = () => window.alert('Feature coming soon')

  const handlePPTExport = async () => {
    if (!hasGenerated || !result) {
      window.alert('Please generate a lesson plan first')
      return
    }
    
    setPptExportLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/api/export/ppt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: lessonTitle,
          subject: formData.subject,
          grade: formData.grade,
          topic: formData.topic,
          lessonContent: result
        })
      })
      
      if (!response.ok) throw new Error('Failed to generate PPT')
      const data = await response.json()
      
      if (data.success) {
        setPptData(data.data)
        setShowPPTModal(true)
      } else {
        throw new Error(data.error || 'Unknown error')
      }
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to generate PPT outline')
    } finally {
      setPptExportLoading(false)
    }
  }

  const insertScriptTemplate = (text: string) => {
    updateActivePhase((phase) => ({
      ...phase,
      script: [...phase.script, { id: createId(), role: 'teacher', content: text }],
    }))
  }

  const smartSuggestions = [
    'What do you observe?',
    'Why do you think this happens?',
    'Discuss with your partner for 30 seconds.',
    'Any different explanations?',
  ]

  return (
    <div className="h-screen overflow-hidden bg-slate-950 text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <header className="relative z-10 h-16 px-6 flex items-center justify-between backdrop-blur-md bg-white/5 border-b border-white/10">
        <div className="flex items-center gap-4 min-w-0">
          <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-xl transition-all text-white/80 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <input
            value={lessonTitle}
            onChange={(e) => {
              setLessonTitle(e.target.value)
              markSaving(phases, result, e.target.value)
            }}
            className="bg-transparent text-lg font-semibold outline-none border border-transparent focus:border-white/10 rounded-xl px-3 py-2 w-[320px] max-w-full"
            placeholder="Lesson title"
          />
        </div>
        <div className="flex items-center gap-2">
          {/* Edit & Regenerate - only show when lesson is generated */}
          {hasGenerated && (
            <button
              onClick={handleOpenEditModal}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 text-sm border border-blue-400/20"
              title="Edit form inputs and regenerate lesson plan"
            >
              <Wand2 className="w-4 h-4" />
              <span className="hidden md:inline">Edit & Regenerate</span>
            </button>
          )}

          {/* History */}
          <button
            onClick={() => setShowHistory(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/80 text-sm border border-white/10"
            title="History"
          >
            <History className="w-4 h-4" />
            <span className="hidden md:inline">History</span>
            {historyList.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-blue-500/30 text-blue-300 text-[10px]">{historyList.length}</span>
            )}
          </button>

          {/* Discard Changes */}
          {hasEdits && (
            <button
              onClick={discardEdits}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-sm border border-amber-400/20"
              title="Discard Changes, restore AI original content"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="hidden md:inline">Discard Changes</span>
            </button>
          )}

          {/* New Lesson */}
          {hasGenerated && (
            <button
              onClick={() => {
                if (!window.confirm('Confirm discard current lesson and start new?')) return
                window.localStorage.removeItem(STORAGE_KEY)
                setPhases([])
                setResult('')
                setOriginalResult('')
                setLessonTitle('Untitled Lesson Plan')
                setFormData({ subject: '', grade: '', topic: '', instructions: '', duration: '45' })
                setSaveState('saved')
                setActiveTab('script')
                setHistory([])
              }}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/80 text-sm border border-white/10"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden md:inline">New</span>
            </button>
          )}

          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/70">
            <Save className="w-4 h-4" />
            {saveState === 'saving' ? 'Auto-saving…' : 'All changes saved'}
          </div>
          <div className="flex items-center gap-3 ml-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-white hidden md:inline">EduSpark</span>
          </div>
        </div>
      </header>

      <main className="relative z-10 h-[calc(100vh-4rem)] flex">
        <aside className="w-60 border-r border-white/10 bg-white/5 backdrop-blur-md flex flex-col overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h2 className="font-semibold">Lesson Flow</h2>
              <p className="text-xs text-white/50">Timeline / Outline / Draggable</p>
            </div>
            <button onClick={() => addPhaseAt()} className="p-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/80" title="Add New Phase">
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {!hasGenerated ? (
              <div className="rounded-2xl border border-dashed border-white/10 p-4 text-sm text-white/45">Structured lesson phases will appear here after generation.</div>
            ) : (
              phases.map((phase, index) => (
                <div key={phase.id} className="relative">
                  {draggingPhaseId && draggingPhaseId !== phase.id && <div className="absolute -top-1 left-5 right-5 h-px bg-blue-400/60" />}
                  <div
                    draggable
                    onDragStart={() => setDraggingPhaseId(phase.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (draggingPhaseId) movePhase(draggingPhaseId, phase.id)
                      setDraggingPhaseId(null)
                    }}
                    onDragEnd={() => setDraggingPhaseId(null)}
                    onClick={() => setActivePhase(phase.id)}
                    className={`rounded-2xl border p-3 cursor-pointer transition-all ${phase.isActive ? 'border-white/20 bg-white/10 shadow-lg shadow-black/20' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="w-4 h-4 mt-1 text-white/35 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-medium text-white truncate">{phase.title}</h3>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowCardMenuId((prev) => (prev === phase.id ? null : phase.id))
                              }}
                              className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10"
                            >
                              <Ellipsis className="w-4 h-4" />
                            </button>
                            {showCardMenuId === phase.id && (
                              <div className="absolute right-0 top-8 z-20 w-36 rounded-xl border border-white/10 bg-slate-900/95 shadow-xl p-1">
                                <button onClick={() => duplicatePhase(phase.id)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-sm">Duplicate</button>
                                <button onClick={() => addPhaseAt(index + 1)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-sm">Insert Below</button>
                                <button onClick={() => deletePhase(phase.id)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-500/10 text-sm text-red-300">Delete</button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className={`px-2 py-1 rounded-full text-[11px] border ${phaseTypeStyles[phase.type]}`}>{phaseTypeLabel[phase.type]}</span>
                          <span className={`px-2 py-1 rounded-full text-[11px] ${statusStyles[phase.status]}`}>{phase.status}</span>
                          <span className="px-2 py-1 rounded-full text-[11px] bg-white/10 text-white/60">{phase.duration} min</span>
                        </div>
                        <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div className={`h-full rounded-full ${phase.type === 'engage' ? 'bg-blue-400' : phase.type === 'inquire' ? 'bg-emerald-400' : phase.type === 'apply' ? 'bg-orange-400' : 'bg-violet-400'}`} style={{ width: `${Math.min(100, (phase.duration / Math.max(totalDuration, 1)) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-white/10 space-y-2 text-sm text-white/70">
            <div className="flex items-center justify-between"><span>Total Duration</span><span className="font-semibold text-white">{totalDuration} min</span></div>
            <div className="flex items-center justify-between"><span>Completed</span><span className="font-semibold text-white">{phases.filter((p) => p.status === 'completed').length}/{phases.length}</span></div>
            <button onClick={undoReorder} disabled={!history.length} className="w-full px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed text-white/80">Undo</button>
          </div>
        </aside>

        <section className="flex-1 overflow-y-auto bg-white/[0.03]">
          <div className="p-6 h-full">
            {!hasGenerated ? (
              <div className="max-w-4xl mx-auto bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-sm mb-4">
                    <Wand2 className="w-4 h-4" />
                    <span>AI Lesson Plan Generator</span>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Create Your Lesson Plan</h1>
                  <p className="text-white/50">Will enter interactive lesson workbench after generation</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><BookOpen className="w-4 h-4 text-blue-400" />Subject *</label>
                    <div className="grid grid-cols-5 gap-2">
                      {subjects.map((s) => (
                        <button key={s.value} type="button" onClick={() => setFormData({ ...formData, subject: s.value })} className={`p-3 rounded-xl border transition-all text-center ${formData.subject === s.value ? 'bg-blue-500 border-blue-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}>
                          <div className="text-xl mb-1">{s.icon}</div>
                          <div className="text-xs truncate">{s.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><GraduationCap className="w-4 h-4 text-purple-400" />Grade Level *</label>
                    <select value={formData.grade} onChange={(e) => setFormData({ ...formData, grade: e.target.value })} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white outline-none" required>
                      <option value="" className="bg-slate-800">Select grade level</option>
                      {grades.map((g) => <option key={g} value={g} className="bg-slate-800">{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><Lightbulb className="w-4 h-4 text-yellow-400" />Lesson Topic *</label>
                    <input type="text" value={formData.topic} onChange={(e) => setFormData({ ...formData, topic: e.target.value })} placeholder="e.g., Wave-Particle Duality, Fractions..." className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 outline-none" required />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><Clock className="w-4 h-4 text-green-400" />Class Duration</label>
                    <div className="flex gap-3">
                      {[{ val: '40', label: '40 min' }, { val: '45', label: '45 min' }, { val: '50', label: '50 min' }, { val: '90', label: '90 min' }].map(({ val, label }) => (
                        <button key={val} type="button" onClick={() => setFormData({ ...formData, duration: val })} className={`flex-1 py-3 rounded-xl border transition-all ${formData.duration === val ? 'bg-green-500 border-green-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><Sparkles className="w-4 h-4 text-pink-400" />Special Requirements (Optional)</label>
                    <textarea value={formData.instructions} onChange={(e) => setFormData({ ...formData, instructions: e.target.value })} rows={3} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 outline-none resize-none" />
                  </div>
                  <button type="submit" disabled={loading || !formData.subject || !formData.grade || !formData.topic} className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" />AI is generating your lesson plan...</> : <><Wand2 className="w-5 h-5" />Generate Lesson Plan</>}
                  </button>
                </form>
              </div>
            ) : activePhase ? (
              <div className="max-w-5xl mx-auto space-y-6">
                <div className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-md">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs border ${phaseTypeStyles[activePhase.type]}`}>{phaseTypeLabel[activePhase.type]}</span>
                        <span className={`px-3 py-1 rounded-full text-xs ${statusStyles[activePhase.status]}`}>{activePhase.status}</span>
                        <span className="px-3 py-1 rounded-full text-xs bg-white/10 text-white/70">{activePhase.duration} min</span>
                      </div>
                      <input value={activePhase.title} onChange={(e) => updateActivePhase((phase) => ({ ...phase, title: e.target.value }))} className="text-2xl font-semibold bg-transparent outline-none w-full" />
                    </div>
                    <button onClick={() => updateActivePhase((phase) => ({ ...phase, status: 'completed' }))} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500/15 text-green-300 border border-green-400/20 hover:bg-green-500/20"><CheckCircle2 className="w-4 h-4" />Mark Complete</button>
                  </div>

                  <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3">
                    {tabOptions.map((tab) => {
                      const Icon = tab.icon
                      return <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-4 py-2 rounded-xl text-sm transition-all flex items-center gap-2 ${activeTab === tab.key ? 'bg-white/15 text-white border border-white/10' : 'text-white/55 hover:text-white hover:bg-white/5'}`}><Icon className="w-4 h-4" />{tab.label}</button>
                    })}
                  </div>

                  {activeTab === 'script' && (
                    <div className="mt-5 space-y-4">
                      <div className="flex flex-wrap gap-2">
                        {smartSuggestions.map((item) => <button key={item} onClick={() => insertScriptTemplate(item)} className="px-3 py-2 rounded-xl bg-blue-500/10 border border-blue-400/20 text-blue-300 text-sm hover:bg-blue-500/15">{item}</button>)}
                      </div>
                      {activePhase.script.map((line) => (
                        <div key={line.id} className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
                          <div className="flex items-center gap-3">
                            <select value={line.role} onChange={(e) => updateActivePhase((phase) => ({ ...phase, script: phase.script.map((item) => item.id === line.id ? { ...item, role: e.target.value as DialogLine['role'] } : item) }))} className={`px-3 py-2 rounded-xl border text-sm outline-none ${roleStyles[line.role]} bg-slate-900/70`}>
                              <option value="teacher">teacher</option><option value="student">student</option><option value="narrator">narrator</option>
                            </select>
                            <button onClick={() => updateActivePhase((phase) => ({ ...phase, script: phase.script.filter((item) => item.id !== line.id) }))} className="p-2 rounded-lg text-white/45 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <textarea value={line.content} onChange={(e) => updateActivePhase((phase) => ({ ...phase, script: phase.script.map((item) => item.id === line.id ? { ...item, content: e.target.value } : item) }))} rows={3} className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-2xl outline-none text-white resize-none" placeholder="Enter teacher/student/narrator script" />
                        </div>
                      ))}
                      <button onClick={() => updateActivePhase((phase) => ({ ...phase, script: [...phase.script, { id: createId(), role: 'teacher', content: '' }] }))} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/80"><Plus className="w-4 h-4" />Add Script Line</button>
                    </div>
                  )}

                  {activeTab === 'activities' && (
                    <div className="mt-5 space-y-4">
                      {activePhase.activities.map((activity) => (
                        <div key={activity.id} className="rounded-2xl border border-white/10 bg-white/5 p-4 grid md:grid-cols-[1fr,120px,140px,auto,auto] gap-3 items-center">
                          <input value={activity.action} onChange={(e) => updateActivePhase((phase) => ({ ...phase, activities: phase.activities.map((item) => item.id === activity.id ? { ...item, action: e.target.value } : item) }))} className="px-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl outline-none" placeholder="Activity Step" />
                          <input type="number" min={1} value={activity.duration} onChange={(e) => updateActivePhase((phase) => ({ ...phase, activities: phase.activities.map((item) => item.id === activity.id ? { ...item, duration: Number(e.target.value) || 0 } : item) }))} className="px-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl outline-none" />
                          <select value={activity.mode} onChange={(e) => updateActivePhase((phase) => ({ ...phase, activities: phase.activities.map((item) => item.id === activity.id ? { ...item, mode: e.target.value as ActivityStep['mode'] } : item) }))} className="px-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl outline-none"><option value="individual">individual</option><option value="pair">pair</option><option value="group">group</option><option value="class">class</option></select>
                          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={activity.completed} onChange={(e) => updateActivePhase((phase) => ({ ...phase, activities: phase.activities.map((item) => item.id === activity.id ? { ...item, completed: e.target.checked } : item) }))} className="rounded" />Done</label>
                          <button onClick={() => updateActivePhase((phase) => ({ ...phase, activities: phase.activities.filter((item) => item.id !== activity.id) }))} className="p-2 rounded-lg text-white/45 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                      <div className="rounded-2xl border border-dashed border-white/10 p-4 text-sm text-white/50">🎬 Video demo placeholder</div>
                      <button onClick={() => updateActivePhase((phase) => ({ ...phase, activities: [...phase.activities, { id: createId(), action: '', duration: 5, mode: 'individual', completed: false }] }))} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/80"><Plus className="w-4 h-4" />Add Activity Step</button>
                    </div>
                  )}

                  {activeTab === 'resources' && (
                    <div className="mt-5 space-y-4">
                      <div className="flex flex-wrap gap-2">
                        <button onClick={exportPlaceholder} className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-sm">Upload File</button>
                        <button onClick={exportPlaceholder} className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-sm">Insert Link</button>
                        <button onClick={exportPlaceholder} className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-sm">Select from Library</button>
                      </div>
                      {activePhase.resources.map((resource) => (
                        <div key={resource.id} className="rounded-2xl border border-white/10 bg-white/5 p-4 grid md:grid-cols-[1fr,1fr,140px,auto] gap-3 items-center">
                          <input value={resource.title} onChange={(e) => updateActivePhase((phase) => ({ ...phase, resources: phase.resources.map((item) => item.id === resource.id ? { ...item, title: e.target.value } : item) }))} className="px-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl outline-none" placeholder="Resource Name" />
                          <input value={resource.url} onChange={(e) => updateActivePhase((phase) => ({ ...phase, resources: phase.resources.map((item) => item.id === resource.id ? { ...item, url: e.target.value } : item) }))} className="px-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl outline-none" placeholder="URL" />
                          <select value={resource.type} onChange={(e) => updateActivePhase((phase) => ({ ...phase, resources: phase.resources.map((item) => item.id === resource.id ? { ...item, type: e.target.value as Resource['type'] } : item) }))} className="px-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl outline-none"><option value="video">video</option><option value="doc">doc</option><option value="link">link</option><option value="tool">tool</option></select>
                          <button onClick={() => updateActivePhase((phase) => ({ ...phase, resources: phase.resources.filter((item) => item.id !== resource.id) }))} className="p-2 rounded-lg text-white/45 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                      <button onClick={() => updateActivePhase((phase) => ({ ...phase, resources: [...phase.resources, { id: createId(), title: '', url: '', type: 'link' }] }))} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/80"><Plus className="w-4 h-4" />Add Resource</button>
                    </div>
                  )}

                  {activeTab === 'design' && (
                    <div className="mt-5 grid md:grid-cols-2 gap-4">
                      {[
                        { key: 'objective', label: 'Learning Objective' },
                        { key: 'keyQuestions', label: 'Key Questions' },
                        { key: 'misconceptions', label: 'Common Misconceptions' },
                        { key: 'notes', label: 'Personal Notes' },
                      ].map((field) => (
                        <div key={field.key} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <label className="block text-sm text-white/65 mb-2">{field.label}</label>
                          <textarea rows={5} value={activePhase.designNote[field.key as keyof DesignNote]} onChange={(e) => updateActivePhase((phase) => ({ ...phase, designNote: { ...phase.designNote, [field.key]: e.target.value } }))} className="w-full px-4 py-3 bg-slate-900/50 border border-white/10 rounded-2xl outline-none resize-none" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
            {error && <div className="max-w-4xl mx-auto mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-2xl"><p className="text-red-300 text-sm">{error}</p></div>}
          </div>
        </section>

        <aside className={`${rightCollapsed ? 'w-16' : 'w-72'} border-l border-white/10 bg-white/5 backdrop-blur-md transition-all duration-300 overflow-hidden`}>
          <div className="h-full flex flex-col">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              {!rightCollapsed && <div><h2 className="font-semibold">Resources & Settings</h2><p className="text-xs text-white/50">Context-aware assistance</p></div>}
              <button onClick={() => setRightCollapsed((prev) => !prev)} className="p-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/75">{rightCollapsed ? <PanelRightOpen className="w-4 h-4" /> : <PanelRightClose className="w-4 h-4" />}</button>
            </div>

            {!rightCollapsed && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                  <button onClick={() => setOpenPanels((prev) => ({ ...prev, resources: !prev.resources }))} className="w-full px-4 py-3 flex items-center justify-between text-left"><span className="font-medium">🎨 Recommended Resources</span><span className="text-white/40">{openPanels.resources ? '−' : '+'}</span></button>
                  {openPanels.resources && (
                    <div className="px-4 pb-4 space-y-3">
                      {(activePhase?.recommendations ?? []).map((resource: LessonResourceRecommendation) => (
                        <div key={resource.id} className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
                          <div className="flex items-center justify-between gap-2 mb-1"><h4 className="text-sm font-medium text-white">{resource.title}</h4><span className="text-[10px] px-2 py-1 rounded-full bg-white/10 text-white/55">{resource.type}</span></div>
                          <p className="text-xs text-white/50 leading-5">{resource.description}</p>
                          <a href={resource.url} target="_blank" rel="noreferrer" className="inline-flex mt-2 text-xs text-blue-300 hover:text-blue-200">View Resource</a>
                        </div>
                      ))}
                      {!activePhase?.recommendations?.length && <div className="text-sm text-white/45">Resources will be recommended after generation</div>}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                  <button onClick={() => setOpenPanels((prev) => ({ ...prev, support: !prev.support }))} className="w-full px-4 py-3 flex items-center justify-between text-left"><span className="font-medium">👥 Differentiation Support</span><span className="text-white/40">{openPanels.support ? '−' : '+'}</span></button>
                  {openPanels.support && (
                    <div className="px-4 pb-4 space-y-3 text-sm text-white/75">
                      {[['struggling', 'Struggling Learners'], ['advanced', 'Advanced Learners'], ['language', 'ELL Support']].map(([key, label]) => (
                        <label key={key} className="flex items-start gap-3">
                          <input 
                            type="checkbox" 
                            checked={supportOptions[key as SupportKey]} 
                            onChange={(e) => { 
                              setSupportOptions((prev) => ({ ...prev, [key]: e.target.checked })); 
                              setShowDifferentiationApply(true);
                              markSaving();
                            }} 
                            className="mt-1" 
                          />
                          {label}
                        </label>
                      ))}
                      
                      {/* Active differentiation indicators */}
                      {hasGenerated && (
                        <div className="pt-2 border-t border-white/10">
                          <p className="text-xs text-white/50 mb-2">Active Strategies:</p>
                          <div className="flex flex-wrap gap-1">
                            {supportOptions.struggling && (
                              <span className="px-2 py-1 rounded-full text-[10px] bg-amber-500/20 text-amber-300 border border-amber-400/30">
                                Scaffolding
                              </span>
                            )}
                            {supportOptions.advanced && (
                              <span className="px-2 py-1 rounded-full text-[10px] bg-purple-500/20 text-purple-300 border border-purple-400/30">
                                Extension
                              </span>
                            )}
                            {supportOptions.language && (
                              <span className="px-2 py-1 rounded-full text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                                ELL Support
                              </span>
                            )}
                            {!supportOptions.struggling && !supportOptions.advanced && !supportOptions.language && (
                              <span className="text-xs text-white/30 italic">None selected</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Apply Differentiation button */}
                      {showDifferentiationApply && hasDifferentiationChanges && (
                        <button
                          onClick={applyDifferentiation}
                          disabled={differentiationLoading}
                          className="w-full mt-3 px-4 py-2 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 text-sm border border-blue-400/30 flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {differentiationLoading ? (
                            <><Loader2 className="w-4 h-4 animate-spin" />Applying...</>
                          ) : (
                            <><Sparkles className="w-4 h-4" />Apply Differentiation</>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                  <button onClick={() => setOpenPanels((prev) => ({ ...prev, settings: !prev.settings }))} className="w-full px-4 py-3 flex items-center justify-between text-left"><span className="font-medium">⚙️ Phase Settings</span><span className="text-white/40">{openPanels.settings ? '−' : '+'}</span></button>
                  {openPanels.settings && (
                    <div className="px-4 pb-4 space-y-4 text-sm text-white/75">
                      <div>
                        <div className="flex items-center justify-between mb-2"><span>Duration</span><span>{activePhase?.duration ?? 5} min</span></div>
                        <input type="range" min={5} max={30} value={activePhase?.duration ?? 5} onChange={(e) => activePhase && updateActivePhase((phase) => ({ ...phase, duration: Number(e.target.value), settings: { ...phase.settings!, interactionMode: phase.settings?.interactionMode || 'class', formativeAssessment: phase.settings?.formativeAssessment || false, standards: phase.settings?.standards || [] } }))} className="w-full" disabled={!activePhase} />
                      </div>
                      <div>
                        <p className="mb-2">Interaction Mode</p>
                        <div className="grid grid-cols-2 gap-2">
                          {['individual', 'pair', 'group', 'class'].map((mode) => (
                            <button 
                              key={mode} 
                              onClick={() => activePhase && updateActivePhase((phase) => ({ 
                                ...phase, 
                                settings: { 
                                  interactionMode: mode as ActivityStep['mode'], 
                                  formativeAssessment: phase.settings?.formativeAssessment || false, 
                                  standards: phase.settings?.standards || [] 
                                } 
                              }))} 
                              className={`px-3 py-2 rounded-xl border text-sm ${(activePhase?.settings?.interactionMode || 'class') === mode ? 'bg-blue-500/20 border-blue-400/30 text-blue-300' : 'bg-white/5 border-white/10 text-white/65'}`}
                            >
                              {mode}
                            </button>
                          ))}
                        </div>
                        
                        {/* Activity suggestions based on selected mode */}
                        {activePhase?.settings?.interactionMode && (
                          <div className="mt-3 p-3 rounded-xl bg-white/5 border border-white/10">
                            <p className="text-xs text-white/50 mb-2">Suggested activities for {activePhase.settings.interactionMode} mode:</p>
                            <div className="flex flex-wrap gap-1">
                              {modeActivitySuggestions[activePhase.settings.interactionMode].slice(0, 4).map((suggestion, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => insertActivitySuggestion(suggestion)}
                                  className="px-2 py-1 rounded-lg bg-blue-500/10 border border-blue-400/20 text-blue-300 text-[11px] hover:bg-blue-500/20 text-left"
                                  title="Click to add this activity"
                                >
                                  + {suggestion}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <label className="flex items-center gap-3"><input type="checkbox" checked={activePhase?.settings?.formativeAssessment || false} onChange={(e) => activePhase && updateActivePhase((phase) => ({ ...phase, settings: { interactionMode: phase.settings?.interactionMode || 'class', formativeAssessment: e.target.checked, standards: phase.settings?.standards || [] } }))} />Include in Formative Assessment</label>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                  <button onClick={() => setOpenPanels((prev) => ({ ...prev, export: !prev.export }))} className="w-full px-4 py-3 flex items-center justify-between text-left"><span className="font-medium">📤 Export Options</span><span className="text-white/40">{openPanels.export ? '−' : '+'}</span></button>
                  {openPanels.export && (
                    <div className="px-4 pb-4 space-y-2">
                      {['Word Document', 'Student Worksheet', 'Parent Summary'].map((item) => (
                        <button key={item} onClick={exportPlaceholder} className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white/75 hover:bg-white/10">
                          <FileDown className="w-4 h-4" />{item}
                        </button>
                      ))}
                      <button 
                        onClick={handlePPTExport} 
                        disabled={pptExportLoading}
                        className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white/75 hover:bg-white/10 disabled:opacity-50"
                      >
                        {pptExportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Presentation className="w-4 h-4" />}
                        PowerPoint Outline
                      </button>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-4 text-xs text-white/45 leading-6">Current model: GPT.<br />Supports auto-save, undo, resource recommendations.</div>
              </div>
            )}
          </div>
        </aside>
      </main>

      {/* History Drawer */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={() => setShowHistory(false)} />
          <div className="w-96 bg-slate-900 border-l border-white/10 flex flex-col h-full shadow-2xl">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">History</h2>
                <p className="text-xs text-white/50 mt-0.5">Recent {MAX_HISTORY} items, click to restore</p>
              </div>
              <button onClick={() => setShowHistory(false)} className="p-2 rounded-xl hover:bg-white/10 text-white/70">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {historyList.length === 0 ? (
                <div className="text-center py-16 text-white/40">
                  <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No History yet</p>
                  <p className="text-sm mt-1">Auto-saved after each generation</p>
                </div>
              ) : (
                historyList.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => restoreFromHistory(item)}
                    className="rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 p-4 cursor-pointer transition-all group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-white truncate">{item.title}</h3>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-500/15 text-blue-300">{item.subject}</span>
                          <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/60">{item.grade}</span>
                          <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/50">{item.formData.duration} min</span>
                        </div>
                        <p className="text-xs text-white/40 mt-2">{formatTime(item.createdAt)} generated</p>
                      </div>
                      <button
                        onClick={(e) => deleteHistoryItem(item.id, e)}
                        className="p-1.5 rounded-lg text-white/30 hover:text-red-300 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all shrink-0"
                        title="Delete this History item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {historyList.length > 0 && (
              <div className="p-4 border-t border-white/10">
                <button
                  onClick={() => {
                    if (!window.confirm('Confirm clear all History?')) return
                    window.localStorage.removeItem(HISTORY_KEY)
                    setHistoryList([])
                  }}
                  className="w-full px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm border border-red-400/20"
                >
                  Clear All History
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* PPT Preview Modal */}
      {showPPTModal && pptData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPPTModal(false)} />
          <div className="relative w-full max-w-4xl max-h-[90vh] bg-slate-900 border border-white/10 rounded-2xl shadow-2xl flex flex-col">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Presentation className="w-5 h-5 text-orange-400" />
                  PowerPoint Outline Preview
                </h2>
                <p className="text-xs text-white/50 mt-0.5">{pptData.slides?.length || 0} slides generated</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const allContent = pptData.slides?.map((slide: any, idx: number) => {
                      const contentText = Array.isArray(slide.content) 
                        ? slide.content.map((c: any) => `• ${typeof c === 'string' ? c : c.text || String(c)}`).join('\n')
                        : ''
                      return `Slide ${slide.number || idx + 1}: ${slide.title || `Slide ${idx + 1}`}\n${contentText}`
                    }).join('\n\n')
                    navigator.clipboard.writeText(allContent)
                    window.alert('All slides copied to clipboard!')
                  }}
                  className="px-4 py-2 rounded-xl bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 text-sm border border-orange-400/30 flex items-center gap-2"
                >
                  <FileDown className="w-4 h-4" />
                  Copy All
                </button>
                <button onClick={() => setShowPPTModal(false)} className="p-2 rounded-xl hover:bg-white/10 text-white/70">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {pptData.slides?.map((slide: any, index: number) => (
                <div key={index} className="rounded-xl border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/20 text-orange-300 flex items-center justify-center text-sm font-medium shrink-0">
                      {slide.number || index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white mb-2">{slide.title || `Slide ${index + 1}`}</h3>
                      {slide.content && slide.content.length > 0 && (
                        <ul className="space-y-1">
                          {slide.content.map((item: any, i: number) => (
                            <li key={i} className="text-sm text-white/70 flex items-start gap-2">
                              <span className="text-orange-400 mt-1">•</span>
                              <span>{typeof item === 'string' ? item : item.text || String(item)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {slide.visual && (
                        <p className="text-xs text-white/40 mt-2 italic">Visual: {slide.visual}</p>
                      )}
                      {slide.notes && (
                        <p className="text-xs text-white/40 mt-1">Notes: {slide.notes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        const contentText = Array.isArray(slide.content) 
                          ? slide.content.map((c: any) => `• ${typeof c === 'string' ? c : c.text || String(c)}`).join('\n')
                          : ''
                        const slideText = `${slide.title || `Slide ${index + 1}`}\n${contentText}`
                        navigator.clipboard.writeText(slideText)
                        window.alert(`Slide ${index + 1} copied!`)
                      }}
                      className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white/70 text-xs border border-white/10"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-white/10 text-xs text-white/50">
              Tip: Copy the content and paste it into PowerPoint to create your presentation.
            </div>
          </div>
        </div>
      )}
      {/* Edit & Regenerate Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowEditModal(false)} />
          <div className="relative w-full max-w-2xl max-h-[90vh] bg-slate-900 border border-white/10 rounded-2xl shadow-2xl flex flex-col">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-blue-400" />
                  Edit & Regenerate
                </h2>
                <p className="text-xs text-white/50 mt-0.5">Modify lesson parameters and regenerate</p>
              </div>
              <button onClick={() => setShowEditModal(false)} className="p-2 rounded-xl hover:bg-white/10 text-white/70">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><BookOpen className="w-4 h-4 text-blue-400" />Subject *</label>
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {subjects.map((s) => (
                    <button 
                      key={s.value} 
                      type="button" 
                      onClick={() => handleEditFormChange('subject', s.value)} 
                      className={`p-2 rounded-xl border transition-all text-center ${editFormData.subject === s.value ? 'bg-blue-500 border-blue-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
                    >
                      <div className="text-lg mb-1">{s.icon}</div>
                      <div className="text-[10px] truncate">{s.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><GraduationCap className="w-4 h-4 text-purple-400" />Grade Level *</label>
                <select 
                  value={editFormData.grade} 
                  onChange={(e) => handleEditFormChange('grade', e.target.value)} 
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white outline-none"
                >
                  <option value="" className="bg-slate-800">Select grade level</option>
                  {grades.map((g) => <option key={g} value={g} className="bg-slate-800">{g}</option>)}
                </select>
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><Lightbulb className="w-4 h-4 text-yellow-400" />Lesson Topic *</label>
                <input 
                  type="text" 
                  value={editFormData.topic} 
                  onChange={(e) => handleEditFormChange('topic', e.target.value)} 
                  placeholder="e.g., Wave-Particle Duality, Fractions..." 
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 outline-none"
                />
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><Clock className="w-4 h-4 text-green-400" />Class Duration</label>
                <div className="flex gap-3">
                  {[{ val: '40', label: '40 min' }, { val: '45', label: '45 min' }, { val: '50', label: '50 min' }, { val: '90', label: '90 min' }].map(({ val, label }) => (
                    <button 
                      key={val} 
                      type="button" 
                      onClick={() => handleEditFormChange('duration', val)} 
                      className={`flex-1 py-3 rounded-xl border transition-all ${editFormData.duration === val ? 'bg-green-500 border-green-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white/80 mb-3"><Sparkles className="w-4 h-4 text-pink-400" />Special Requirements (Optional)</label>
                <textarea 
                  value={editFormData.instructions} 
                  onChange={(e) => handleEditFormChange('instructions', e.target.value)} 
                  rows={3} 
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 outline-none resize-none"
                />
              </div>
            </div>
            
            <div className="p-5 border-t border-white/10 flex items-center justify-between">
              <button 
                onClick={() => setShowEditModal(false)} 
                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/80"
              >
                Cancel
              </button>
              <button 
                onClick={handleRegenerateClick}
                disabled={!editFormData.subject || !editFormData.grade || !editFormData.topic || loading}
                className="px-6 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Generating...</>
                ) : (
                  <><Wand2 className="w-4 h-4" />Regenerate Lesson Plan</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Regenerate */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowConfirmDialog(false)} />
          <div className="relative w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl shadow-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                <RotateCcw className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">Replace Current Lesson Plan?</h3>
                <p className="text-sm text-white/60 mb-4">
                  You have unsaved edits in your current lesson plan. Regenerating will replace all your current content with a new AI-generated lesson plan based on your updated parameters.
                </p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowConfirmDialog(false)} 
                    className="flex-1 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/80"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={submitRegenerate}
                    className="flex-1 px-4 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-400/30"
                  >
                    Replace & Regenerate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LessonPlan
